import React, { memo } from 'react';
import { Accordion, Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { withWindow } from '@shared/utils/withWindow';
import { travelOfferDiagFromApiJson } from '@shared/types/TravelOfferDiag.validation';

import type { ComparisonRowView, DetailItemView, DynamicMarginProps, OtaLinkMode, OtaLinkView, StepCardView } from './DynamicMargin.types';
import {
  DASH,
  calculateDifference,
  createDetailItem,
  formatBooleanValue,
  formatCalculatedMoneyValue,
  formatMoneyValue,
  formatPercentValue,
  formatProviderLabel,
  formatTextValue,
  getOtaLinks,
} from './DynamicMargin.utils';
import { useHotelOfferDiagnostics } from './HotelOfferDiagnosticsContext';

const SECTION_RADIUS = pxToRem(12);
const SECTION_BORDER_COLOR = 'mono3';
const SECTION_BACKGROUND_COLOR = 'white';
const SUBSECTION_BACKGROUND_COLOR = 'mono1';
const SUMMARY_LABEL_COLUMN_WIDTH = pxToRem(132);
const SUMMARY_GRID_GAP = pxToRem(12);
const STEP_BADGE_SIZE = pxToRem(36);
const OTA_LINK_MAX_WIDTH = pxToRem(240);

const OTA_PROVIDER_DISPLAY_LABELS = {
  booking: 'Booking',
  expedia: 'Expedia',
} as const;

const stopPropagation = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const stopParentCardNavigation = (event: React.MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement | null;
  const clickedInnerLink = target?.closest('a');

  if (clickedInnerLink && event.currentTarget.contains(clickedInnerLink)) {
    event.stopPropagation();
    return;
  }

  event.preventDefault();
  event.stopPropagation();
};

const SectionCard = ({ children, title }: Readonly<{ children: React.ReactNode; title: string }>) => (
  <Box
    padding={4}
    width="100%"
    border="1px solid"
    borderColor={SECTION_BORDER_COLOR}
    borderRadius={SECTION_RADIUS}
    backgroundColor={SECTION_BACKGROUND_COLOR}
  >
    <TextV2
      typographyVariant="label2"
      fontWeight={600}
      color="mono14"
    >
      {title}
    </TextV2>
    <Box marginTop={3}>{children}</Box>
  </Box>
);

const SubsectionCard = ({ children, title }: Readonly<{ children: React.ReactNode; title: string }>) => (
  <Box
    padding={4}
    width="100%"
    border="1px solid"
    borderColor={SECTION_BORDER_COLOR}
    borderRadius={SECTION_RADIUS}
    backgroundColor={SUBSECTION_BACKGROUND_COLOR}
  >
    <TextV2
      typographyVariant="label2"
      fontWeight={600}
      color="mono14"
    >
      {title}
    </TextV2>
    <Box marginTop={3}>{children}</Box>
  </Box>
);

type ComparisonTableProps = Readonly<{
  columnLabels: ReadonlyArray<string>;
  rows: ReadonlyArray<ComparisonRowView>;
  title: string;
}>;

const ComparisonTable = memo<ComparisonTableProps>(({ columnLabels, rows, title }) => {
  const desktopGridTemplate = `${SUMMARY_LABEL_COLUMN_WIDTH} repeat(${columnLabels.length}, minmax(0, 1fr))`;
  const mobileGridTemplate = `repeat(${Math.min(2, columnLabels.length)}, minmax(0, 1fr))`;

  return (
    <SectionCard title={title}>
      <Box
        display={{ _: 'none', sm2: 'grid' }}
        gridTemplateColumns={desktopGridTemplate}
        columnGap={SUMMARY_GRID_GAP}
        paddingBottom={3}
      >
        <Box />
        {columnLabels.map(label => (
          <TextV2
            key={label}
            typographyVariant="label3"
            fontWeight={600}
            color="mono10"
          >
            {label}
          </TextV2>
        ))}
      </Box>

      {rows.map(row => (
        <Box
          key={row.label}
          paddingY={3}
          borderTop="1px solid"
          borderColor={SECTION_BORDER_COLOR}
        >
          <Box
            display={{ _: 'none', sm2: 'grid' }}
            gridTemplateColumns={desktopGridTemplate}
            columnGap={SUMMARY_GRID_GAP}
            alignItems="start"
          >
            <TextV2
              typographyVariant="label2"
              fontWeight={row.isEmphasized ? 600 : 500}
              color="mono14"
            >
              {row.label}
            </TextV2>
            {row.values.map((value, valueIndex) => (
              <TextV2
                key={`${row.label}-${columnLabels[valueIndex]}`}
                typographyVariant="label2"
                fontWeight={row.isEmphasized ? 600 : 400}
                color="mono14"
              >
                {value}
              </TextV2>
            ))}
          </Box>

          <Box
            display={{ _: 'flex', sm2: 'none' }}
            flexDirection="column"
            rowGap={2}
          >
            <TextV2
              typographyVariant="label2"
              fontWeight={600}
              color="mono14"
            >
              {row.label}
            </TextV2>
            <Box
              display="grid"
              gridTemplateColumns={mobileGridTemplate}
              columnGap={3}
              rowGap={3}
            >
              {row.values.map((value, valueIndex) => (
                <Flex
                  key={`${row.label}-mobile-${columnLabels[valueIndex]}`}
                  flexDirection="column"
                  rowGap={1}
                >
                  <TextV2
                    typographyVariant="label4"
                    color="mono8"
                  >
                    {columnLabels[valueIndex]}
                  </TextV2>
                  <TextV2
                    typographyVariant="label2"
                    fontWeight={row.isEmphasized ? 600 : 400}
                    color="mono14"
                  >
                    {value}
                  </TextV2>
                </Flex>
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </SectionCard>
  );
});
ComparisonTable.displayName = 'ComparisonTable';

type StepCardProps = Readonly<StepCardView>;

const StepCard = memo<StepCardProps>(({ description, index, isHighlighted = false, title, value }) => (
  <Box
    padding={4}
    width="100%"
    border="1px solid"
    borderColor={SECTION_BORDER_COLOR}
    borderRadius={SECTION_RADIUS}
    backgroundColor={isHighlighted ? 'mono2' : SECTION_BACKGROUND_COLOR}
  >
    <Flex
      flexDirection={{ _: 'column', sm2: 'row' }}
      justifyContent="space-between"
      alignItems={{ _: 'flex-start', sm2: 'flex-start' }}
      gap={3}
    >
      <Flex
        alignItems="flex-start"
        gap={3}
      >
        <Flex
          width={STEP_BADGE_SIZE}
          height={STEP_BADGE_SIZE}
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor="mono4"
          borderRadius="999px"
          backgroundColor="white"
          flexShrink={0}
        >
          <TextV2
            typographyVariant="label2"
            fontWeight={600}
            color="mono10"
          >
            {index}
          </TextV2>
        </Flex>
        <Flex
          flexDirection="column"
          gap={1}
        >
          <TextV2
            typographyVariant="hed3"
            fontWeight={600}
            color="mono14"
          >
            {title}
          </TextV2>
          {description ? (
            <TextV2
              typographyVariant="label2"
              color="mono10"
            >
              {description}
            </TextV2>
          ) : null}
        </Flex>
      </Flex>
      <TextV2
        typographyVariant="hed2"
        fontWeight={600}
        color="mono14"
      >
        {value}
      </TextV2>
    </Flex>
  </Box>
));
StepCard.displayName = 'StepCard';

type StepWalkthroughProps = Readonly<{
  steps: ReadonlyArray<StepCardView>;
  title: string;
}>;

const StepWalkthrough = memo<StepWalkthroughProps>(({ steps, title }) => (
  <SectionCard title={title}>
    <Flex
      flexDirection="column"
      gap={3}
    >
      {steps.map(step => (
        <StepCard
          key={step.title}
          {...step}
        />
      ))}
    </Flex>
  </SectionCard>
));
StepWalkthrough.displayName = 'StepWalkthrough';

const DetailRows = memo<Readonly<{ items: ReadonlyArray<DetailItemView> }>>(({ items }) => (
  <Flex
    flexDirection="column"
    gap={3}
  >
    {items.map((item, index) => (
      <Flex
        key={item.label}
        flexDirection={{ _: 'column', sm2: 'row' }}
        justifyContent="space-between"
        alignItems={{ _: 'flex-start', sm2: 'center' }}
        gap={1}
        paddingTop={index === 0 ? 0 : 3}
        borderTop={index === 0 ? 'none' : '1px solid'}
        borderColor={SECTION_BORDER_COLOR}
      >
        <TextV2
          typographyVariant="label2"
          fontWeight={500}
          color="mono14"
        >
          {item.label}
        </TextV2>
        <TextV2
          typographyVariant="label2"
          color="mono14"
        >
          {item.value}
        </TextV2>
      </Flex>
    ))}
  </Flex>
));
DetailRows.displayName = 'DetailRows';

type DetailListProps = Readonly<{
  items: ReadonlyArray<DetailItemView>;
  title: string;
}>;

const DetailList = memo<DetailListProps>(({ items, title }) =>
  items.length ? (
    <SubsectionCard title={title}>
      <DetailRows items={items} />
    </SubsectionCard>
  ) : null,
);
DetailList.displayName = 'DetailList';

type SyntheticStrikeoutSectionProps = Readonly<{
  items: ReadonlyArray<DetailItemView>;
  title: string;
}>;

/**
 * TRAV-3970. Top-level rather than tucked into "Advanced details": for a card showing "Save 5%", whether
 * that came from a provider or from us is the first thing worth knowing, not a detail.
 */
const SyntheticStrikeoutSection = memo<SyntheticStrikeoutSectionProps>(({ items, title }) =>
  items.length ? (
    <SectionCard title={title}>
      <DetailRows items={items} />
    </SectionCard>
  ) : null,
);
SyntheticStrikeoutSection.displayName = 'SyntheticStrikeoutSection';

type OtaLinkRowProps = Readonly<{
  href: string;
  label: string;
  otaLinkMode: OtaLinkMode;
}>;

const OtaLinkRow = memo<OtaLinkRowProps>(({ href, label, otaLinkMode }) => {
  const normalizedHref = ensureHttpsPrefix(href);
  const displayLabel = OTA_PROVIDER_DISPLAY_LABELS[label as keyof typeof OTA_PROVIDER_DISPLAY_LABELS] ?? label;

  if (otaLinkMode === 'native') {
    return (
      <Flex
        flexDirection={{ _: 'column', sm2: 'row' }}
        justifyContent="space-between"
        alignItems={{ _: 'flex-start', sm2: 'center' }}
        gap={1}
      >
        <TextV2
          typographyVariant="label2"
          fontWeight={500}
          color="mono14"
        >
          {displayLabel}
        </TextV2>
        <Box
          as="a"
          href={normalizedHref}
          target="_blank"
          rel="noopener noreferrer"
          maxWidth={OTA_LINK_MAX_WIDTH}
          title={href}
        >
          <TextV2
            typographyVariant="label2"
            color="linkText"
            style={{ overflowWrap: 'anywhere', textDecoration: 'underline' }}
          >
            {displayLabel}
          </TextV2>
        </Box>
      </Flex>
    );
  }

  const handleOpen = (event: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    withWindow(global => {
      global.open(normalizedHref, '_blank', 'noopener,noreferrer');
    });
  };

  return (
    <Flex
      flexDirection={{ _: 'column', sm2: 'row' }}
      justifyContent="space-between"
      alignItems={{ _: 'flex-start', sm2: 'center' }}
      gap={1}
    >
      <TextV2
        typographyVariant="label2"
        fontWeight={500}
        color="mono14"
      >
        {displayLabel}
      </TextV2>
      <Box
        as="span"
        maxWidth={OTA_LINK_MAX_WIDTH}
        role="link"
        tabIndex={0}
        cursor="pointer"
        title={href}
        onClick={handleOpen}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleOpen(event);
          }
        }}
      >
        <TextV2
          typographyVariant="label2"
          color="linkText"
          style={{ overflowWrap: 'anywhere', textDecoration: 'underline' }}
        >
          {displayLabel}
        </TextV2>
      </Box>
    </Flex>
  );
});
OtaLinkRow.displayName = 'OtaLinkRow';

type OtaLinksSectionProps = Readonly<{
  links: ReadonlyArray<OtaLinkView>;
  otaLinkMode: OtaLinkMode;
  title: string;
}>;

const OtaLinksSection = memo<OtaLinksSectionProps>(({ links, otaLinkMode, title }) => {
  if (!links.length) {
    return null;
  }

  return (
    <SubsectionCard title={title}>
      <Flex
        flexDirection="column"
        gap={3}
      >
        {links.map((link, index) => (
          <Box
            key={link.label}
            paddingTop={index === 0 ? 0 : 3}
            borderTop={index === 0 ? 'none' : '1px solid'}
            borderColor={SECTION_BORDER_COLOR}
          >
            <OtaLinkRow
              href={link.href}
              label={link.label}
              otaLinkMode={otaLinkMode}
            />
          </Box>
        ))}
      </Flex>
    </SubsectionCard>
  );
});
OtaLinksSection.displayName = 'OtaLinksSection';

type OfferMarginSectionProps = Readonly<{
  columnLabels: ReadonlyArray<string>;
  detailItems: ReadonlyArray<DetailItemView>;
  detailTitle: string;
  rows: ReadonlyArray<ComparisonRowView>;
  title: string;
}>;

const OfferMarginSection = memo<OfferMarginSectionProps>(({ columnLabels, detailItems, detailTitle, rows, title }) => (
  <Flex
    flexDirection="column"
    gap={3}
  >
    <ComparisonTable
      title={title}
      columnLabels={columnLabels}
      rows={rows}
    />
    <DetailList
      title={detailTitle}
      items={detailItems}
    />
  </Flex>
));
OfferMarginSection.displayName = 'OfferMarginSection';

export const DynamicMargin = memo<DynamicMarginProps>(({ diag, rawDiag, otaLinkMode = 'native' }) => {
  const { showMargin } = useHotelOfferDiagnostics();
  const { tq } = useTranslation('stays');
  const { formatCurrency } = useCurrencyFormatter();
  const parsedDiag = React.useMemo(() => (showMargin && !diag ? travelOfferDiagFromApiJson(rawDiag) : null), [diag, rawDiag, showMargin]);
  const resolvedDiag = diag ?? parsedDiag;

  if (!showMargin || !resolvedDiag) {
    return null;
  }

  const yesLabel = tq`Yes`;
  const noLabel = tq`No`;
  // A synthesis-only payload has no margin blob to inherit a currency from. It should still carry one of
  // its own; fall back rather than render every amount as a dash if an older server omits it.
  const currencyCode = resolvedDiag.currency ?? DEFAULT_CURRENCY;
  const nightsLabel = resolvedDiag.nights === 1 ? tq`1 Night` : tq`${resolvedDiag.nights ?? 0} Nights`;
  const summaryColumnLabels = [tq`Winner`, tq`Published Price`, tq`2nd`, tq`2nd Published Price`];
  const offerMarginColumnLabels = [tq`Before markup`, tq`After markup`];
  const winnerProvider = formatProviderLabel(resolvedDiag.winner?.provider, resolvedDiag.winner?.supplierSource);
  const secondBestProvider = formatProviderLabel(resolvedDiag.secondBest?.provider, resolvedDiag.secondBest?.supplierSource);
  const spreadMarkupPercent = resolvedDiag.margin?.markupPercentOfSpread ?? resolvedDiag.spreadAdjustment?.markupPercentOfSpread;
  const otaLinks = getOtaLinks(resolvedDiag.otaUrls);
  // A margin decision is no longer what makes _diag non-null (TRAV-3970), so the margin sections are only
  // drawn when one actually ran — otherwise a synthesized card would show tables of em-dashes.
  const hasMarginSections = Boolean(resolvedDiag.margin);
  const syntheticStrikeout = resolvedDiag.syntheticStrikeout;

  const summaryRows: ReadonlyArray<ComparisonRowView> = [
    {
      label: tq`Provider`,
      values: [winnerProvider, winnerProvider, secondBestProvider, secondBestProvider],
    },
    {
      label: tq`Nightly`,
      values: [
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.perOccupancyNightlyBase),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.perOccupancyNightlyOriginalBase),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.perOccupancyNightlyBase),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.perOccupancyNightlyOriginalBase),
      ],
    },
    {
      label: nightsLabel,
      values: [
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.base),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.originalBase),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.base),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.originalBase),
      ],
    },
    {
      label: tq`Taxes & Fees`,
      values: [
        formatCalculatedMoneyValue(formatCurrency, currencyCode, calculateDifference(resolvedDiag.winner?.total, resolvedDiag.winner?.base)),
        formatCalculatedMoneyValue(formatCurrency, currencyCode, calculateDifference(resolvedDiag.winner?.originalTotal, resolvedDiag.winner?.originalBase)),
        formatCalculatedMoneyValue(formatCurrency, currencyCode, calculateDifference(resolvedDiag.secondBest?.total, resolvedDiag.secondBest?.base)),
        formatCalculatedMoneyValue(formatCurrency, currencyCode, calculateDifference(resolvedDiag.secondBest?.originalTotal, resolvedDiag.secondBest?.originalBase)),
      ],
    },
    {
      label: tq`Total`,
      values: [
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.total),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner?.originalTotal),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.total),
        formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.secondBest?.originalTotal),
      ],
      isEmphasized: true,
    },
  ];

  const rawSpreadExplanation =
    resolvedDiag.winner?.total && resolvedDiag.spreadAdjustment?.effectiveCompetitorTotal
      ? `${formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment.effectiveCompetitorTotal)} - ${formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner.total)}`
      : undefined;

  const spreadCapExplanation =
    resolvedDiag.winner?.total && resolvedDiag.spreadAdjustment?.maxSpreadPercentUsed !== undefined
      ? `${formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.winner.total)} × ${formatPercentValue(resolvedDiag.spreadAdjustment.maxSpreadPercentUsed)}`
      : undefined;

  const finalSpreadExplanation =
    resolvedDiag.spreadAdjustment?.rawSpread && resolvedDiag.spreadAdjustment?.spreadCap
      ? `min(${formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment.rawSpread)}, ${formatMoneyValue(
          formatCurrency,
          currencyCode,
          resolvedDiag.spreadAdjustment.spreadCap,
        )})`
      : undefined;

  const totalMarkupExplanation =
    resolvedDiag.spreadAdjustment?.finalSpread && spreadMarkupPercent !== undefined
      ? `${formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment.finalSpread)} × ${formatPercentValue(spreadMarkupPercent)}`
      : undefined;

  const steps: ReadonlyArray<StepCardView> = [
    {
      index: 1,
      title: tq`Raw Spread`,
      value: formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.rawSpread),
      description: rawSpreadExplanation,
    },
    {
      index: 2,
      title: tq`Spread Cap`,
      value: formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.spreadCap),
      description: spreadCapExplanation,
    },
    {
      index: 3,
      title: tq`Final Spread`,
      value: formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.finalSpread),
      description: finalSpreadExplanation,
    },
    {
      index: 4,
      title: tq`Total Markup`,
      value: formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.margin?.markup),
      description: totalMarkupExplanation,
      isHighlighted: true,
    },
  ];

  const additionalDetailItems = [
    createDetailItem(tq`Comparison total used`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.effectiveCompetitorTotal)),
    createDetailItem(tq`Comparison total / night`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.effectiveCompetitorNightly)),
    createDetailItem(tq`Best published price`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.bestPublishedPrice)),
    createDetailItem(tq`Best published price / night`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.spreadAdjustment?.bestPublishedPriceNightly)),
    createDetailItem(tq`Published price provider`, formatTextValue(resolvedDiag.spreadAdjustment?.bestPublishedPriceProvider)),
    createDetailItem(tq`Published price used`, formatBooleanValue(resolvedDiag.spreadAdjustment?.usedPublishedPriceReference, yesLabel, noLabel)),
    createDetailItem(tq`Spread was capped`, formatBooleanValue(resolvedDiag.spreadAdjustment?.spreadWasCapped, yesLabel, noLabel)),
    createDetailItem(tq`Cap percent used`, formatPercentValue(resolvedDiag.spreadAdjustment?.maxSpreadPercentUsed)),
    createDetailItem(tq`Markup share of spread`, formatPercentValue(spreadMarkupPercent)),
    createDetailItem(tq`Markup / night`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.margin?.markupNightly)),
  ].filter((item): item is DetailItemView => Boolean(item));

  const syntheticStrikeoutDetailItems = syntheticStrikeout
    ? [
        createDetailItem(tq`Synthesized by Joy`, formatBooleanValue(syntheticStrikeout.applied, yesLabel, noLabel)),
        // Only meaningful on a decline, and the whole reason the section is drawn for one.
        createDetailItem(tq`Skip reason`, syntheticStrikeout.applied ? DASH : formatTextValue(syntheticStrikeout.skipReason)),
        createDetailItem(tq`Anchor price`, formatMoneyValue(formatCurrency, currencyCode, syntheticStrikeout.anchorPrice)),
        createDetailItem(tq`Synthesized strikeout`, formatMoneyValue(formatCurrency, currencyCode, syntheticStrikeout.anchorStrikeout)),
        createDetailItem(tq`Implied discount`, formatPercentValue(syntheticStrikeout.impliedPercent)),
        createDetailItem(tq`Configured discount`, formatPercentValue(syntheticStrikeout.discountPercent)),
        createDetailItem(tq`Rounded to whole unit`, formatBooleanValue(syntheticStrikeout.roundedToMajorUnit, yesLabel, noLabel)),
        createDetailItem(tq`Surface`, formatTextValue(syntheticStrikeout.surface)),
      ].filter((item): item is DetailItemView => Boolean(item))
    : [];

  const offerMarginRows: ReadonlyArray<ComparisonRowView> = resolvedDiag.offerMargin
    ? [
        {
          label: tq`Base`,
          values: [
            formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.preMarkupBase),
            formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.postMarkupBase),
          ],
        },
        {
          label: tq`Total`,
          values: [
            formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.preMarkupTotal),
            formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.postMarkupTotal),
          ],
          isEmphasized: true,
        },
      ]
    : [];

  const offerMarginDetailItems = resolvedDiag.offerMargin
    ? [
        createDetailItem(tq`Markup`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.markup)),
        createDetailItem(tq`Markup / night`, formatMoneyValue(formatCurrency, currencyCode, resolvedDiag.offerMargin.markupNightly)),
        createDetailItem(tq`Distribution mode`, formatTextValue(resolvedDiag.offerMargin.distributionMode)),
      ].filter((item): item is DetailItemView => Boolean(item))
    : [];

  return (
    <Flex
      flexDirection="column"
      gap={3}
      marginTop={2}
      width="100%"
      onClick={stopParentCardNavigation}
      onTouchEnd={stopPropagation}
      onTouchMove={stopPropagation}
      onTouchStart={stopPropagation}
    >
      <SyntheticStrikeoutSection
        title={tq`Synthetic strikeout`}
        items={syntheticStrikeoutDetailItems}
      />

      {/* Every section below is derived from the margin decision, down to the OTA links — which the server
          only resolves when one exists — so with no margin decision there is nothing here to draw. */}
      {hasMarginSections ? (
        <>
          <ComparisonTable
            title={tq`Comparison summary`}
            columnLabels={summaryColumnLabels}
            rows={summaryRows}
          />

          <StepWalkthrough
            title={tq`Spread walkthrough`}
            steps={steps}
          />

          <Box
            width="100%"
            border="1px solid"
            borderColor={SECTION_BORDER_COLOR}
            borderRadius={SECTION_RADIUS}
            backgroundColor={SECTION_BACKGROUND_COLOR}
            overflow="hidden"
          >
            <Accordion>
              <Accordion.Item>
                <Accordion.Button
                  width="100%"
                  padding={4}
                  _hover={{ backgroundColor: SECTION_BACKGROUND_COLOR, color: 'mono14' }}
                >
                  <TextV2
                    typographyVariant="label2"
                    fontWeight={600}
                    color="mono14"
                  >
                    {tq`Advanced details`}
                  </TextV2>
                  <Accordion.Icon style={{ marginLeft: 'auto' }} />
                </Accordion.Button>
                <Accordion.Panel
                  shouldWatchForHeightChanges
                  paddingX={4}
                  paddingBottom={4}
                >
                  <Flex
                    flexDirection="column"
                    gap={3}
                  >
                    {resolvedDiag.offerMargin ? (
                      <OfferMarginSection
                        title={tq`Offer markup`}
                        columnLabels={offerMarginColumnLabels}
                        detailItems={offerMarginDetailItems}
                        detailTitle={tq`Offer markup details`}
                        rows={offerMarginRows}
                      />
                    ) : null}
                    <DetailList
                      title={tq`Additional details`}
                      items={additionalDetailItems}
                    />
                    <OtaLinksSection
                      title={tq`OTA links`}
                      links={otaLinks}
                      otaLinkMode={otaLinkMode}
                    />
                  </Flex>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Box>
        </>
      ) : null}
    </Flex>
  );
});

DynamicMargin.displayName = 'DynamicMargin';
